// Strings for the Bulk Actions wizard (Changes page). Kept separate from
// EmployeeActions.translations.js so the wizard stays self-contained.
//
// `messages` translates the validation codes returned by
// POST /employee-actions/bulk/validate. {placeholders} are filled from the
// message params; unknown codes fall back to the server's English text.

const bulkTranslations = {
  en: {
    openButton: "Bulk Actions",
    title: "Bulk Actions",
    subtitle: "Apply salary adjustments, resignations and terminations to many employees from one Excel file.",
    steps: { choose: "Choose actions", upload: "Upload file", review: "Review", submit: "Submit" },
    stepOf: "Step {n} of 4",

    // Step 1
    chooseTitle: "What do you want to do?",
    chooseHint: "Pick one or more. You can combine them in the same file — it's optional.",
    chooseHintSingle: "This is the action your admin level can perform in bulk.",
    actions: {
      salary_change: { title: "Salary Adjustment", desc: "Update gross, net and insurance salary. Tax and payroll are recalculated." },
      resignation: { title: "Resignation", desc: "Resignation date, last working day, reason and rehire eligibility. Final payroll is prorated." },
      terminate: { title: "Termination", desc: "Last working day, termination type and reason. Final payroll is prorated." },
    },
    selected: "Selected",
    notAllowed: "You don't have permission for this action",
    downloadTemplate: "Download template",
    downloadingTemplate: "Preparing template…",
    templateLanguage: "Template language",
    haveFile: "I already have a file",
    templateTip: "One sheet per chosen action, each with only its own columns, dropdowns, and an Instructions sheet.",

    // Step 2
    uploadTitle: "Upload your file",
    dropHere: "Drag & drop your Excel file here",
    or: "or",
    browse: "Browse files",
    accepted: "Accepted: .xlsx, .xls, .csv · up to {max} rows",
    rowsFound: "{n} rows found",
    replaceFile: "Replace file",
    removeFile: "Remove",
    parsing: "Reading file…",
    validating: "Checking every row…",
    errEmpty: "The file has no data rows.",
    errType: "Please upload an .xlsx, .xls or .csv file.",
    errColumns: "Missing required columns: {cols}. Download the template to get the right headers.",
    errTooMany: "This file has {n} rows — the limit is {max}.",
    errRead: "The file couldn't be read. Make sure it's a valid Excel file.",
    errSheetColumns: "Sheet \"{sheet}\" is missing required columns: {cols}.",
    errNoSheets: "No action sheets found. Use the downloaded template (Salary Adjustments / Resignations / Terminations).",

    // Step 3
    reviewTitle: "Review before submitting",
    reviewHint: "Nothing has been saved yet. Rows with errors are skipped; fix them in Excel and upload again if needed.",
    total: "Total",
    valid: "Ready",
    warning: "Warnings",
    error: "Errors",
    filterAll: "All",
    search: "Search code or name…",
    colRow: "Row",
    colSheet: "Sheet",
    colEmployee: "Employee",
    colAction: "Action",
    colChange: "Change",
    colStatus: "Status",
    statusValid: "Ready",
    statusWarning: "Warning",
    statusError: "Error",
    notFound: "Not found",
    appliesNow: "Applies now",
    scheduledFor: "Scheduled {date}",
    net: "Net",
    gross: "Gross",
    insurance: "Insurance",
    tax: "Monthly tax",
    current: "Current",
    new: "New",
    lastWorkingDay: "Last working day",
    terminationType: "Type",
    resignationDate: "Resignation date",
    resignationReason: "Reason",
    eligibleForRehire: "Eligible for rehire",
    yes: "Yes",
    no: "No",
    payrollProrated: "That month's payroll is prorated to the last working day; later unpaid payrolls are removed.",
    stateChange: "State: {from} → Inactive",
    source: { gross: "from Gross", net: "from Net", insurance: "insurance only — Net kept" },
    showMore: "Show {n} more",
    noRows: "No rows match this filter.",
    downloadErrors: "Download error report",

    // Confirm
    submitN: "Submit {n} actions",
    submit1: "Submit 1 action",
    confirmTitle: "Submit {n} actions?",
    confirmNow: "{n} apply immediately",
    confirmScheduled: "{n} are scheduled for a future date",
    confirmApproval: "{n} will go through the approval workflow",
    confirmSkipped: "{n} rows with errors will be skipped",
    confirmYes: "Yes, submit",

    // Step 4
    submittingTitle: "Creating actions…",
    submittingHint: "You can minimize this and keep working — we'll keep going in the background.",
    minimize: "Minimize",
    progress: "{done} of {total}",
    doneTitle: "Bulk upload finished",
    doneWithErrors: "Finished with some issues",
    failedTitle: "Nothing was created",
    interrupted: "The upload was interrupted (server restart). Rows already created are listed below.",
    outcomes: {
      completed: "Completed",
      scheduled: "Scheduled",
      pending_approval: "Pending approval",
      failed: "Failed",
      skipped: "Skipped",
      queued: "Queued",
      processing: "Processing",
      created: "Created",
    },
    downloadResults: "Download results (.xlsx)",
    done: "Done",
    newUpload: "Upload another file",
    actionNo: "Action #{id}",

    // Footer / generic
    back: "Back",
    next: "Next",
    cancel: "Cancel",
    close: "Close",
    retry: "Try again",
    discardTitle: "Discard this upload?",
    discardBody: "Your validated file will be cleared.",
    discard: "Discard",
    keep: "Keep editing",
    loading: "Loading…",
    genericError: "Something went wrong. Please try again.",
    floatingTitle: "Bulk actions",
    open: "Open",

    types: {
      salary_change: "Salary Adjustment",
      resignation: "Resignation",
      terminate: "Termination",
    },
    adjustmentTypes: {
      annual_increase: "Annual increase", promotion: "Promotion", market_adjustment: "Market adjustment",
      performance_bonus: "Performance based", cost_of_living: "Cost of living", correction: "Correction",
    },
    terminationTypes: {
      voluntary: "Voluntary resignation", involuntary: "Involuntary termination", retirement: "Retirement",
      contract_end: "Contract end", mutual_agreement: "Mutual agreement", layoff: "Layoff",
    },
    resignationReasons: {
      better_opportunity: "Better opportunity", personal: "Personal reasons", relocation: "Relocation",
      studies: "Studies", health: "Health", retirement: "Retirement", other: "Other",
    },

    messages: {
      ACTION_TYPE_REQUIRED: "Action Type is required",
      ACTION_TYPE_INVALID: "Unknown Action Type \"{value}\"",
      EMPLOYEE_CODE_REQUIRED: "Client Code is required",
      REASON_REQUIRED: "Reason is required",
      EFFECTIVE_DATE_REQUIRED: "Effective Date is required",
      EFFECTIVE_DATE_INVALID: "Effective Date \"{value}\" isn't a valid date (use DD/MM/YYYY)",
      GROSS_INVALID: "New Gross Salary isn't a number",
      NET_INVALID: "New Net Salary isn't a number",
      INSURANCE_INVALID: "New Insurance Salary isn't a number",
      GROSS_NOT_POSITIVE: "New Gross Salary must be greater than 0",
      NET_NOT_POSITIVE: "New Net Salary must be greater than 0",
      INSURANCE_NOT_POSITIVE: "New Insurance Salary must be greater than 0",
      SALARY_VALUE_REQUIRED: "Fill at least one of New Gross, New Net or New Insurance Salary",
      GROSS_AND_NET_BOTH: "Gross and Net both filled — Gross is used, Net recalculated",
      ADJUSTMENT_TYPE_UNKNOWN: "Unknown Adjustment Type \"{value}\" — left empty",
      LWD_REQUIRED: "Last Working Day is required",
      LWD_INVALID: "Last Working Day \"{value}\" isn't a valid date (use DD/MM/YYYY)",
      TERMINATION_TYPE_UNKNOWN: "Unknown Termination Type \"{value}\" — default used",
      EMPLOYEE_AMBIGUOUS: "Client Code \"{code}\" matches more than one employee",
      EMPLOYEE_NOT_FOUND: "No employee with code \"{code}\"",
      ACTION_NOT_ALLOWED: "You don't have permission for this action type",
      EMPLOYEE_INACTIVE: "Employee is already inactive",
      NAME_MISMATCH: "Name in file \"{fileName}\" differs from \"{dbName}\"",
      DUPLICATE_ROW: "Duplicate for this employee (also on row {otherRow})",
      OPEN_ACTION_EXISTS: "Employee already has an open action (#{actionId}, {status})",
      SALARY_AFTER_LWD: "Effective Date is after the Last Working Day ({lwd}) in this file",
      NO_CURRENT_SALARY: "Employee has no current salary — fill New Gross or New Net",
      NO_CHANGE: "New values equal the current salary",
      BIG_CHANGE: "Net salary changes by {pct}%",
      INSURANCE_ABOVE_GROSS: "Insurance Salary is higher than Gross Salary",
      PAID_PAYROLL: "Payroll {period} is already paid and won't change",
      LWD_BEFORE_HIRE: "Last Working Day is before the hiring date ({hired})",
      LWD_FAR_FUTURE: "Last Working Day is {days} days from today",
      RESIGNATION_DATE_INVALID: "Resignation Date \"{value}\" isn't a valid date (use DD/MM/YYYY)",
      RESIGNATION_AFTER_LWD: "Resignation Date is after the Last Working Day",
      RESIGNATION_REASON_UNKNOWN: "Unknown Resignation Reason \"{value}\" — left empty",
      RESIGNATION_REASON_REQUIRED: "Choose a Resignation Reason or fill Details",
      REHIRE_INVALID: "Eligible for Rehire \"{value}\" should be Yes or No — left empty",
      TERMINATION_TYPE_REQUIRED: "Termination Type is required",
      TERMINATION_TYPE_INVALID: "Termination Type \"{value}\" isn't valid",
      TERMINATION_TYPE_VOLUNTARY: "Voluntary exits go on the Resignations sheet, not Terminations",
    },
  },

  ar: {
    openButton: "إجراءات جماعية",
    title: "الإجراءات الجماعية",
    subtitle: "طبّق تعديلات الرواتب والاستقالات وإنهاء الخدمة لعدد كبير من الموظفين من ملف Excel واحد.",
    steps: { choose: "اختيار الإجراءات", upload: "رفع الملف", review: "المراجعة", submit: "الإرسال" },
    stepOf: "الخطوة {n} من 4",

    chooseTitle: "ماذا تريد أن تفعل؟",
    chooseHint: "اختر إجراءً أو أكثر. يمكنك الجمع بينها في نفس الملف — اختياري.",
    chooseHintSingle: "هذا هو الإجراء المسموح لمستواك الإداري تنفيذه بشكل جماعي.",
    actions: {
      salary_change: { title: "تعديل الراتب", desc: "تحديث الإجمالي والصافي والأجر التأميني مع إعادة حساب الضريبة والرواتب." },
      resignation: { title: "استقالة", desc: "تاريخ تقديم الاستقالة وآخر يوم عمل والسبب وأهلية إعادة التوظيف، ويُحتسب آخر راتب نسبيًا." },
      terminate: { title: "إنهاء الخدمة", desc: "آخر يوم عمل ونوع إنهاء الخدمة والسبب، ويُحتسب آخر راتب نسبيًا." },
    },
    selected: "مُختار",
    notAllowed: "ليست لديك صلاحية لهذا الإجراء",
    downloadTemplate: "تحميل القالب",
    downloadingTemplate: "جاري تجهيز القالب…",
    templateLanguage: "لغة القالب",
    haveFile: "لدي ملف بالفعل",
    templateTip: "ورقة لكل إجراء مختار بأعمدته فقط، مع قوائم منسدلة وورقة تعليمات.",

    uploadTitle: "ارفع ملفك",
    dropHere: "اسحب ملف Excel وأفلته هنا",
    or: "أو",
    browse: "استعراض الملفات",
    accepted: "الصيغ المقبولة: .xlsx و .xls و .csv · حتى {max} صف",
    rowsFound: "تم العثور على {n} صف",
    replaceFile: "استبدال الملف",
    removeFile: "إزالة",
    parsing: "جاري قراءة الملف…",
    validating: "جاري فحص كل صف…",
    errEmpty: "لا يحتوي الملف على صفوف بيانات.",
    errType: "يرجى رفع ملف بصيغة .xlsx أو .xls أو .csv.",
    errColumns: "أعمدة مطلوبة ناقصة: {cols}. حمّل القالب للحصول على العناوين الصحيحة.",
    errTooMany: "يحتوي الملف على {n} صف — الحد الأقصى {max}.",
    errRead: "تعذّرت قراءة الملف. تأكد أنه ملف Excel صالح.",
    errSheetColumns: "الورقة \"{sheet}\" تنقصها أعمدة مطلوبة: {cols}.",
    errNoSheets: "لم يتم العثور على أوراق إجراءات. استخدم القالب الذي تم تحميله (تعديلات الرواتب / الاستقالات / إنهاء الخدمة).",

    reviewTitle: "راجع قبل الإرسال",
    reviewHint: "لم يُحفظ أي شيء بعد. الصفوف التي بها أخطاء سيتم تخطيها؛ صحّحها في Excel وأعد الرفع إذا لزم.",
    total: "الإجمالي",
    valid: "جاهز",
    warning: "تحذيرات",
    error: "أخطاء",
    filterAll: "الكل",
    search: "ابحث بالكود أو الاسم…",
    colRow: "الصف",
    colSheet: "الورقة",
    colEmployee: "الموظف",
    colAction: "الإجراء",
    colChange: "التغيير",
    colStatus: "الحالة",
    statusValid: "جاهز",
    statusWarning: "تحذير",
    statusError: "خطأ",
    notFound: "غير موجود",
    appliesNow: "يُطبّق الآن",
    scheduledFor: "مجدول في {date}",
    net: "الصافي",
    gross: "الإجمالي",
    insurance: "التأميني",
    tax: "الضريبة الشهرية",
    current: "الحالي",
    new: "الجديد",
    lastWorkingDay: "آخر يوم عمل",
    terminationType: "النوع",
    resignationDate: "تاريخ تقديم الاستقالة",
    resignationReason: "السبب",
    eligibleForRehire: "مؤهل لإعادة التوظيف",
    yes: "نعم",
    no: "لا",
    payrollProrated: "يُحتسب راتب ذلك الشهر حتى آخر يوم عمل، وتُحذف الرواتب اللاحقة غير المدفوعة.",
    stateChange: "الحالة: {from} ← غير نشط",
    source: { gross: "من الإجمالي", net: "من الصافي", insurance: "التأميني فقط — الصافي ثابت" },
    showMore: "عرض {n} أخرى",
    noRows: "لا توجد صفوف مطابقة.",
    downloadErrors: "تحميل تقرير الأخطاء",

    submitN: "إرسال {n} إجراء",
    submit1: "إرسال إجراء واحد",
    confirmTitle: "إرسال {n} إجراء؟",
    confirmNow: "{n} تُطبّق فورًا",
    confirmScheduled: "{n} مجدولة لتاريخ لاحق",
    confirmApproval: "{n} ستمر بمسار الموافقات",
    confirmSkipped: "سيتم تخطي {n} صف بها أخطاء",
    confirmYes: "نعم، أرسل",

    submittingTitle: "جاري إنشاء الإجراءات…",
    submittingHint: "يمكنك تصغير هذه النافذة ومتابعة عملك — سنكمل في الخلفية.",
    minimize: "تصغير",
    progress: "{done} من {total}",
    doneTitle: "اكتمل الرفع الجماعي",
    doneWithErrors: "اكتمل مع بعض المشكلات",
    failedTitle: "لم يتم إنشاء أي إجراء",
    interrupted: "توقف الرفع (إعادة تشغيل الخادم). الصفوف التي أُنشئت بالفعل موضحة أدناه.",
    outcomes: {
      completed: "مكتمل",
      scheduled: "مجدول",
      pending_approval: "بانتظار الموافقة",
      failed: "فشل",
      skipped: "تم التخطي",
      queued: "في الانتظار",
      processing: "قيد المعالجة",
      created: "تم الإنشاء",
    },
    downloadResults: "تحميل النتائج (.xlsx)",
    done: "تم",
    newUpload: "رفع ملف آخر",
    actionNo: "إجراء رقم {id}",

    back: "رجوع",
    next: "التالي",
    cancel: "إلغاء",
    close: "إغلاق",
    retry: "إعادة المحاولة",
    discardTitle: "تجاهل هذا الرفع؟",
    discardBody: "سيتم مسح الملف الذي تم فحصه.",
    discard: "تجاهل",
    keep: "متابعة",
    loading: "جاري التحميل…",
    genericError: "حدث خطأ ما. يرجى المحاولة مرة أخرى.",
    floatingTitle: "الإجراءات الجماعية",
    open: "فتح",

    types: {
      salary_change: "تعديل الراتب",
      resignation: "استقالة",
      terminate: "إنهاء الخدمة",
    },
    adjustmentTypes: {
      annual_increase: "زيادة سنوية", promotion: "ترقية", market_adjustment: "تعديل وفق السوق",
      performance_bonus: "حسب الأداء", cost_of_living: "غلاء المعيشة", correction: "تصحيح",
    },
    terminationTypes: {
      voluntary: "استقالة طوعية", involuntary: "إنهاء خدمة", retirement: "تقاعد",
      contract_end: "انتهاء العقد", mutual_agreement: "اتفاق متبادل", layoff: "تسريح",
    },
    resignationReasons: {
      better_opportunity: "فرصة أفضل", personal: "أسباب شخصية", relocation: "انتقال",
      studies: "دراسة", health: "صحية", retirement: "تقاعد", other: "أخرى",
    },

    messages: {
      ACTION_TYPE_REQUIRED: "نوع الإجراء مطلوب",
      ACTION_TYPE_INVALID: "نوع إجراء غير معروف \"{value}\"",
      EMPLOYEE_CODE_REQUIRED: "كود العميل مطلوب",
      REASON_REQUIRED: "السبب مطلوب",
      EFFECTIVE_DATE_REQUIRED: "تاريخ السريان مطلوب",
      EFFECTIVE_DATE_INVALID: "تاريخ السريان \"{value}\" غير صالح (استخدم DD/MM/YYYY)",
      GROSS_INVALID: "الراتب الإجمالي الجديد ليس رقمًا",
      NET_INVALID: "صافي الراتب الجديد ليس رقمًا",
      INSURANCE_INVALID: "الأجر التأميني الجديد ليس رقمًا",
      GROSS_NOT_POSITIVE: "الراتب الإجمالي الجديد يجب أن يكون أكبر من صفر",
      NET_NOT_POSITIVE: "صافي الراتب الجديد يجب أن يكون أكبر من صفر",
      INSURANCE_NOT_POSITIVE: "الأجر التأميني الجديد يجب أن يكون أكبر من صفر",
      SALARY_VALUE_REQUIRED: "أدخل الإجمالي أو الصافي أو الأجر التأميني الجديد على الأقل",
      GROSS_AND_NET_BOTH: "تم إدخال الإجمالي والصافي — يُستخدم الإجمالي ويُعاد حساب الصافي",
      ADJUSTMENT_TYPE_UNKNOWN: "نوع تعديل غير معروف \"{value}\" — تُرك فارغًا",
      LWD_REQUIRED: "آخر يوم عمل مطلوب",
      LWD_INVALID: "آخر يوم عمل \"{value}\" غير صالح (استخدم DD/MM/YYYY)",
      TERMINATION_TYPE_UNKNOWN: "نوع إنهاء غير معروف \"{value}\" — استُخدم الافتراضي",
      EMPLOYEE_AMBIGUOUS: "الكود \"{code}\" يطابق أكثر من موظف",
      EMPLOYEE_NOT_FOUND: "لا يوجد موظف بالكود \"{code}\"",
      ACTION_NOT_ALLOWED: "ليست لديك صلاحية لهذا النوع من الإجراءات",
      EMPLOYEE_INACTIVE: "الموظف غير نشط بالفعل",
      NAME_MISMATCH: "الاسم في الملف \"{fileName}\" يختلف عن \"{dbName}\"",
      DUPLICATE_ROW: "مكرر لنفس الموظف (أيضًا في الصف {otherRow})",
      OPEN_ACTION_EXISTS: "لدى الموظف إجراء مفتوح بالفعل (#{actionId}، {status})",
      SALARY_AFTER_LWD: "تاريخ السريان بعد آخر يوم عمل ({lwd}) في نفس الملف",
      NO_CURRENT_SALARY: "لا يوجد راتب حالي — أدخل الإجمالي أو الصافي الجديد",
      NO_CHANGE: "القيم الجديدة مساوية للراتب الحالي",
      BIG_CHANGE: "يتغير صافي الراتب بنسبة {pct}%",
      INSURANCE_ABOVE_GROSS: "الأجر التأميني أعلى من الراتب الإجمالي",
      PAID_PAYROLL: "راتب {period} مدفوع بالفعل ولن يتغير",
      LWD_BEFORE_HIRE: "آخر يوم عمل قبل تاريخ التعيين ({hired})",
      LWD_FAR_FUTURE: "آخر يوم عمل بعد {days} يومًا من اليوم",
      RESIGNATION_DATE_INVALID: "تاريخ تقديم الاستقالة \"{value}\" غير صالح (استخدم DD/MM/YYYY)",
      RESIGNATION_AFTER_LWD: "تاريخ تقديم الاستقالة بعد آخر يوم عمل",
      RESIGNATION_REASON_UNKNOWN: "سبب استقالة غير معروف \"{value}\" — تُرك فارغًا",
      RESIGNATION_REASON_REQUIRED: "اختر سبب الاستقالة أو املأ التفاصيل",
      REHIRE_INVALID: "قيمة أهلية إعادة التوظيف \"{value}\" يجب أن تكون نعم أو لا — تُركت فارغة",
      TERMINATION_TYPE_REQUIRED: "نوع إنهاء الخدمة مطلوب",
      TERMINATION_TYPE_INVALID: "نوع إنهاء الخدمة \"{value}\" غير صالح",
      TERMINATION_TYPE_VOLUNTARY: "الخروج الطوعي يُسجَّل في ورقة الاستقالات وليس إنهاء الخدمة",
    },
  },
};

/** Fill {placeholders}. */
export const fmt = (template, params = {}) =>
  String(template ?? "").replace(/\{(\w+)\}/g, (_, k) => (params[k] ?? params[k] === 0 ? String(params[k]) : `{${k}}`));

/** Translate one validation message from the server. */
export const translateBulkMessage = (bt, m) =>
  bt.messages[m.code] ? fmt(bt.messages[m.code], m.params) : m.text;

export default bulkTranslations;
